import { isEmpty, isNil, noop, get } from 'lodash';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';

import { ANALYTICS_EVENTS, ORDER_TYPES } from '@nandosaus/constants';
import { analytics } from '@analytics';
import { navigation } from '@constants';
import { useFeature } from '@hooks/use-feature';
import { Button } from '@components/button/new';
import { Box, Flex } from '@components/grid';
import { P } from '@components/typography/new';
import { Image } from '@components/image';
import { DefaultImage } from '@components/default/default-image';
import { ReorderModal } from '@components/reorder-modal';
import { Icon } from '@components/icons';
import { useRootStore } from '@nandosaus/state-management';
import { CartClearModal } from '@components/cart-clear-modal';
import { CancelCateringOrderModal } from '@components/cancel-catering-order-modal';

// eslint-disable-next-line react/prop-types
const Tag = ({ children, ...rest }) => {
  return (
    <P
      variant="small-semi-bold"
      width="fit-content"
      borderRadius="38px"
      backgroundColor="grey60"
      color="white"
      px="8px"
      py="0.5px"
      {...rest}
    >
      {children}
    </P>
  );
};

const ShowcaseImage = styled(Image)`
  object-fit: cover;
`;

const PastOrder = observer(
  ({
    order,
    onReorder,
    catering,
    isAccountPage,
    handleClick,
    displayAsList,
    showIsRefundedTag,
    deliveryInfo,
    pickupInfo,
  }) => {
    const {
      formattedSimpleDate,
      formattedTotal,
      formattedOrderItems,
      defaultFormattedOrderItems,
      defaultCateringFormattedOrderItems,
      isRefunded,
      status,
    } = order;

    // On the account page and order help, MenuStore.menu may not be loaded (no restaurant selected),
    // so use defaultFormattedOrderItems which is backed by MenuStore.defaultMenu instead.
    const getResolvedOrderItems = () => {
      if (catering) return defaultCateringFormattedOrderItems;
      if (isAccountPage || displayAsList) return defaultFormattedOrderItems;
      return formattedOrderItems;
    };
    const resolvedOrderItems = getResolvedOrderItems();

    const router = useRouter();
    const { OrderingContextStore, MenuStore, CartStore, PastOrdersStore, DeliveryStore } = useRootStore();

    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showCartClearModal, setShowCartClearModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [cancelingSuccess, setCancelingSuccess] = useState(false);
    const [cancelError, setCancelError] = useState(null);

    const enableReorder = useFeature('reorder');

    // Single-line item summary + a hero thumbnail (replaces the multi-image strip). Derived from the
    // context-resolved items so it works on the account page where the live menu isn't loaded.
    const orderItemNames = resolvedOrderItems
      .map(({ product }) => product?.name)
      .filter(Boolean)
      .join(', ');
    const heroItem = order.highestCostItemWithImage;
    const showcaseImage = heroItem?.image || heroItem?.resolvedImage || null;

    const reorder = async () => {
      // A member already in catering delivery with an address keeps it — the reorder only adds items.
      const hadCateringDeliveryAddress =
        OrderingContextStore.orderType === ORDER_TYPES.CATERING_DELIVERY && !isNil(DeliveryStore.deliveryAddress);

      order.addOrderItemsToCart(catering);

      // When the reorder switched the member into catering delivery, restore the order's saved
      // delivery address into DeliveryStore — otherwise the catering-delivery restaurant never
      // resolves (it's address-derived) and validation downstream throws trying to read status
      // off an undefined restaurant.
      if (
        order.isCateringDeliveryOrder &&
        order.deliveryAddress &&
        OrderingContextStore.orderType === ORDER_TYPES.CATERING_DELIVERY &&
        !hadCateringDeliveryAddress
      ) {
        await DeliveryStore.setDeliveryAddress(
          {
            street: order.deliveryAddress.address1,
            suburb: order.deliveryAddress.suburb,
            state: order.deliveryAddress.state,
            postcode: order.deliveryAddress.postcode,
            unit: order.deliveryAddress.address2 || null,
            latitude: Number(order.deliveryAddress.latitude),
            longitude: Number(order.deliveryAddress.longitude),
          },
          { saveToPreviousSearch: false }
        );
      }

      if (!order.isAvailableAtCurrentRestaurant) {
        return;
      }
      setIsLoading(true);

      // Navigate to catering page for catering orders. `cateringReorder` tells the catering page to
      // open the Catering Order Details drawer once, so the member re-confirms order type, date and
      // time — a delivery reorder restores the address above, which otherwise passes the page's
      // isValid check and skips the drawer.
      if (catering) {
        router.push({
          pathname: navigation.CATERING,
          query: 'showCart&cateringReorder',
        });
      } else {
        router.push({
          pathname: navigation.MENU,
          query: 'showCart',
        });
      }

      setTimeout(() => {
        setIsLoading(false);
        // Call the callback after reorder completes
        if (onReorder) {
          onReorder();
        }
      }, 300);
    };

    const handleReorder = () => {
      analytics.track(ANALYTICS_EVENTS.REORDER_CLICKED);

      // For catering orders, check if cart has non-catering items or restaurant is set to non-catering.
      // Both CATERING and CATERING_DELIVERY are catering order types and share the catering menu, so
      // reordering into either should not trigger the "switching order types" modal.
      const isCateringOrderType = orderType =>
        orderType === ORDER_TYPES.CATERING || orderType === ORDER_TYPES.CATERING_DELIVERY;
      const hasItemsFromOtherCart = catering
        ? CartStore.itemCount > 0 && MenuStore.menuType && MenuStore.menuType !== 'catering'
        : CartStore.itemCount > 0 && MenuStore.menuType && MenuStore.menuType === 'catering';
      const hasWrongRestaurantType = catering
        ? get(OrderingContextStore?.restaurant, 'name', null) && !isCateringOrderType(OrderingContextStore.orderType)
        : get(OrderingContextStore?.restaurant, 'name', null) && isCateringOrderType(OrderingContextStore.orderType);

      if (hasItemsFromOtherCart || hasWrongRestaurantType) {
        setShowCartClearModal(true);
        return;
      }

      reorder();
    };

    const startEditOrder = () => {
      // Clear current order and set values of editing order
      OrderingContextStore.reset();
      CartStore.clear();

      const timeMoment = moment(order.pickupTime);
      const timeWithOffset = timeMoment.format('HH:mm:ssZ');

      const combinedDateTime = `${moment(order.pickupTime).format('YYYY-MM-DD')}T${timeWithOffset}`;
      const dateTime = moment(combinedDateTime).format();

      OrderingContextStore.updateSettings({
        cateringPickupDate: moment(order.pickupTime).format('YYYY-MM-DD'),
        cateringPickupTime: dateTime,
        cateringHeadcount: order.cateringHeadcount || 0,
        orderType: order.isCateringDeliveryOrder ? ORDER_TYPES.CATERING_DELIVERY : ORDER_TYPES.CATERING,
        restaurantId: order.restaurantDetails?.id,
      });

      // Restore the order's saved delivery address into DeliveryStore — otherwise the address
      // shown in the edit flow is whatever was last in DeliveryStore from an unrelated session,
      // not this order's actual address.
      if (order.isCateringDeliveryOrder && order.deliveryAddress) {
        DeliveryStore.setDeliveryAddress(
          {
            street: order.deliveryAddress.address1,
            suburb: order.deliveryAddress.suburb,
            state: order.deliveryAddress.state,
            postcode: order.deliveryAddress.postcode,
            unit: order.deliveryAddress.address2 || null,
            latitude: Number(order.deliveryAddress.latitude),
            longitude: Number(order.deliveryAddress.longitude),
          },
          { saveToPreviousSearch: false }
        );
      }

      MenuStore.setMenuType('catering');

      order.addOrderItemsToCart(true);

      CartStore.setOrderUuid(order.id);

      router.push({
        pathname: navigation.CATERING,
      });
    };

    const handleCancelOrder = async () => {
      setIsCancelling(true);
      setCancelError(null);
      try {
        const response = await order.cancelCateringOrder();
        if (response?.data?.cancelCateringOrder?.success) {
          setCancelingSuccess(true);
        } else {
          const errorMessage =
            response?.data?.cancelCateringOrder?.message || 'Failed to cancel order. Please try again.';
          setCancelError(errorMessage);
        }
      } catch (error) {
        const errorMessage = error?.message || 'Unable to cancel order';
        setCancelError(errorMessage);
      } finally {
        setIsCancelling(false);
      }
    };

    if (isEmpty(resolvedOrderItems)) {
      return null;
    }

    const isOrderInactive =
      (isRefunded && showIsRefundedTag) ||
      status === 'cancelled' ||
      status === 'submitted_to_restaurant' ||
      status === 'error';

    // On the menu page the tiles sit in a horizontal scroll carousel: ~3 across but never narrower
    // than 350px (flexShrink 0 so the min-width holds and the carousel scrolls instead). The width
    // subtracts the carousel's 24px gap (2 gaps across 3 tiles = 16px each) so 3 fit without overflow.
    // The account list stays full-width; the order-help selector (displayAsList) keeps its auto width.
    const isMenuTile = !isAccountPage && !displayAsList;
    let tileWidth;
    if (isAccountPage) {
      tileWidth = '100%';
    } else if (isMenuTile) {
      tileWidth = 'calc(33.333% - 16px)';
    }

    return (
      <>
        <Box
          bg="white"
          borderRadius="16px"
          p="16px"
          width={tileWidth}
          minWidth={isMenuTile ? '350px' : undefined}
          flexShrink={isMenuTile ? 0 : undefined}
          border={isAccountPage ? '1px solid' : undefined}
          borderColor={isAccountPage ? 'warmWhite3' : undefined}
        >
          <Flex gap="16px" height="100%" alignItems={displayAsList ? 'center' : 'flex-start'}>
            <Box width="80px" height="80px" borderRadius="8px" overflow="hidden" flexShrink={0}>
              {showcaseImage ? (
                <ShowcaseImage src={showcaseImage} alt={orderItemNames} width="100%" height="100%" />
              ) : (
                <DefaultImage width="100%" height="100%" />
              )}
            </Box>

            <Flex
              flexDirection="column"
              gap="8px"
              justifyContent="space-between"
              flex="1"
              height="100%"
              minWidth={0}
              width="100%"
            >
              <Flex flexDirection="column" gap="12px">
                <Flex flexDirection="column" gap="4px" minWidth={0}>
                  {isOrderInactive && (isRefunded || status === 'cancelled') && (
                    <Flex gap="8px" mb="4px">
                      {isRefunded && showIsRefundedTag && <Tag backgroundColor="skyBlue">Refunded</Tag>}
                      {status === 'cancelled' && <Tag>Cancelled</Tag>}
                    </Flex>
                  )}
                  <P
                    variant="small-semi-bold"
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {orderItemNames}
                  </P>
                  <Flex alignItems="center" gap="8px">
                    {/* No total ($0, or RedCat-only history) shows the date alone, without a leading divider */}
                    {formattedTotal && (
                      <>
                        <P variant="small" color="grey70">
                          {formattedTotal}
                        </P>
                        <Box width="1px" height="8px" backgroundColor="grey50" flexShrink={0} />
                      </>
                    )}
                    <P variant="small" color="grey70">
                      {formattedSimpleDate}
                    </P>
                  </Flex>
                </Flex>

                {deliveryInfo && (
                  <Flex
                    alignSelf="flex-start"
                    alignItems="center"
                    gap="8px"
                    bg="warmWhite2"
                    borderRadius="8px"
                    px="12px"
                    py="4px"
                    maxWidth="100%"
                  >
                    <P variant="small">{`Delivery from ${deliveryInfo.storeName}`}</P>
                    <Box width="1px" height="8px" backgroundColor="grey50" flexShrink={0} />
                    <P variant="small">{`${deliveryInfo.date}, ETA ${deliveryInfo.eta}`}</P>
                  </Flex>
                )}

                {pickupInfo && (
                  <Flex
                    alignSelf="flex-start"
                    alignItems="center"
                    gap="8px"
                    bg="warmWhite2"
                    borderRadius="8px"
                    px="12px"
                    py="4px"
                    maxWidth="100%"
                  >
                    <P variant="small">{`Pickup from ${pickupInfo.storeName}`}</P>
                    <Box width="1px" height="8px" backgroundColor="grey50" flexShrink={0} />
                    <P variant="small">{pickupInfo.dateTime}</P>
                  </Flex>
                )}

                {!displayAsList && order.pointsEarned > 0 && (
                  <Flex alignItems="center" gap="4px">
                    <Icon name="periPerksLogo" height="18px" width="24px" stroke="none" />
                    <P as="span" variant="small" fontWeight="bold" color="customerRewards3">
                      {`+${order.pointsEarned.toFixed(2)} pts`}
                    </P>
                  </Flex>
                )}
              </Flex>

              {!displayAsList && (
                <Flex alignItems="center" justifyContent="space-between" gap="16px">
                  <Button
                    style={{ height: 'fit-content', padding: 0 }}
                    onClick={() => setIsOpen(true)}
                    variant="link"
                    py={{ _: 0, md: 0, lg: 0 }}
                  >
                    <P textDecoration="underline" textTransform="none" color="black" variant="text-link">
                      Show details
                    </P>
                  </Button>
                  {enableReorder && (
                    <Button
                      isLoading={isLoading}
                      size="small"
                      variant="secondary"
                      widthType="fit"
                      onClick={handleReorder}
                    >
                      Reorder
                    </Button>
                  )}
                </Flex>
              )}
            </Flex>

            {displayAsList && (
              <Box
                as="button"
                type="button"
                onClick={() => handleClick(order)}
                p="0"
                border="none"
                backgroundColor="transparent"
                style={{ cursor: 'pointer' }}
                flexShrink={0}
              >
                <Icon name="chevronRight" width="24px" height="24px" />
              </Box>
            )}
          </Flex>
        </Box>

        <ReorderModal
          handleReorder={handleReorder}
          modalOpen={isOpen}
          handleModalClose={() => setIsOpen(false)}
          orderId={order.id}
          catering={catering}
          canReorder={!displayAsList}
          startEditOrder={catering ? startEditOrder : undefined}
          handleCancelOrder={catering ? () => setShowCancelModal(true) : undefined}
          isCateringCutoffPassed={catering ? order.isCateringCutoffPassed : false}
          isOrderInactive={
            catering
              ? order.isRefunded ||
                order.status === 'cancelled' ||
                order.status === 'submitted_to_restaurant' ||
                order.status === 'error'
              : false
          }
          isAccountPage
        />

        <CartClearModal
          open={showCartClearModal}
          returnButtonText="Cancel"
          continueButtonText="Reorder anyway"
          onReturnToOriginal={() => {
            setShowCartClearModal(false);
          }}
          onContinueToSecondary={() => {
            setShowCartClearModal(false);
            // Call reorder directly rather than re-running handleReorder: the member has just
            // confirmed, so the order-type check would only reopen this modal.
            reorder();
          }}
        />

        {catering && (
          <CancelCateringOrderModal
            open={showCancelModal}
            action={handleCancelOrder}
            onClose={() => {
              setShowCancelModal(false);
              if (cancelingSuccess) {
                setCancelingSuccess(false);
                PastOrdersStore.loadCateringOrders();
              }
              setCancelError(null);
            }}
            isLoading={isCancelling}
            order={order}
            success={cancelingSuccess}
            error={cancelError}
          />
        )}
      </>
    );
  }
);

PastOrder.propTypes = {
  order: PropTypes.shape({
    formattedSimpleDate: PropTypes.string.isRequired,
    productNamesJoined: PropTypes.string.isRequired,
    orderItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    formattedOrderItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    formattedForCart: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    highestCostItemWithImage: PropTypes.shape({ image: PropTypes.string, name: PropTypes.string }).isRequired,
    pointsEarned: PropTypes.number,
  }).isRequired,
  displayAsList: PropTypes.bool,
  showIsRefundedTag: PropTypes.bool,
  handleClick: PropTypes.func,
  catering: PropTypes.bool,
  onReorder: PropTypes.func,
  isAccountPage: PropTypes.bool,
  // Delivery info pill ("Delivery from {store} | {date}, ETA {eta}") — shown when this tile is used
  // for an active catering-delivery order that isn't trackable yet.
  deliveryInfo: PropTypes.shape({
    storeName: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    eta: PropTypes.string.isRequired,
  }),
  // Pickup info pill ("Pickup from {store} | {date} at {time}") — shown when this tile is used for
  // an active catering-pickup order that isn't trackable yet.
  pickupInfo: PropTypes.shape({
    storeName: PropTypes.string.isRequired,
    dateTime: PropTypes.string.isRequired,
  }),
};

PastOrder.defaultProps = {
  displayAsList: false,
  showIsRefundedTag: false,
  handleClick: noop,
  onReorder: undefined,
  catering: false,
  isAccountPage: false,
  deliveryInfo: null,
  pickupInfo: null,
};

export { PastOrder };
